import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import { useSelector } from 'react-redux';
import { IST_ZONE, in_zone, zone_label } from './ist_time';

/**
 * Runs as a read-only table; editing lives in the Manage modal.
 *
 * ONE ROW PER RUN, not per slot — every action in the last column operates on the
 * run. Slots stack inside the Time, UTC and Meeting link cells, aligned by line.
 */

// Fixed widths: an elastic link column squeezed Actions until its buttons wrapped.
const COLUMNS = 'minmax(140px, 1.1fr) 90px 165px minmax(130px, 1.3fr) 125px 265px';
const ROW_MIN = 1015;

// Completed and empty are derived server-side and arrive resolved, so nothing here
// holds any date logic.
export const BADGES = {
  draft: { label: '○ Draft', bg: 'var(--status-closed-bg)', fg: 'var(--status-closed-text)' },
  scheduled: {
    label: '● Scheduled',
    bg: 'var(--state-success-bg)',
    fg: 'var(--state-success-text)'
  },
  completed: { label: '● Completed', bg: 'var(--surface-sunken)', fg: 'var(--text-muted)' },
  // Published but every slot has been cancelled — the landing page serves nothing.
  empty: {
    label: '● No active slots',
    bg: 'var(--state-warning-bg)',
    fg: 'var(--state-warning-text)'
  },
  cancelled: { label: '● Cancelled', bg: 'var(--state-error-bg)', fg: 'var(--state-error-text)' }
};

export const RUN_STATUS_FILTERS = [
  { label: 'All', value: 'all' },
  { label: 'Draft', value: 'draft' },
  { label: 'Scheduled', value: 'scheduled' },
  { label: 'Completed', value: 'completed' },
  { label: 'No active slots', value: 'empty' },
  { label: 'Cancelled', value: 'cancelled' }
];

// common.css pads every button 10px 20px, which is too wide for three in one cell.
const COMPACT_BTN = { padding: '4px 10px', fontSize: 12, whiteSpace: 'nowrap' };

const ist_time = (iso) => moment(iso).tz(IST_ZONE).format('h:mm A');

/** Stacked cell: one line per slot, so Time / UTC / Link stay aligned by index. */
function SlotLines({ slots, children }) {
  if (slots.length === 0) {
    return (
      <span className="text-sm" style={{ color: 'var(--text-muted)' }}>
        —
      </span>
    );
  }
  return (
    <div className="flex flex-col gap-1" style={{ minWidth: 0 }}>
      {slots.map(children)}
    </div>
  );
}

SlotLines.propTypes = { slots: PropTypes.array.isRequired, children: PropTypes.func.isRequired };

export default function RunsTable({ runs, onManage, onDuplicate, onCancelRun, onDeleteRun }) {
  const now = moment();
  // The second time column follows the header dropdown. It is read-only, so it
  // cannot affect what is stored; the slot input stays hard-wired to IST.
  const zone = useSelector((state) => state.home.iana_timezone) || 'UTC';
  if (runs.length === 0) {
    return (
      <div
        className="rounded-lg p-6 text-center text-sm"
        style={{
          background: 'var(--surface-card)',
          border: '1px solid var(--border-default)',
          color: 'var(--text-muted)'
        }}
      >
        No runs match this filter.
      </div>
    );
  }

  return (
    <div
      className="rounded-lg"
      style={{
        background: 'var(--surface-card)',
        border: '1px solid var(--border-default)',
        boxShadow: 'var(--shadow-card)',
        overflowX: 'auto'
      }}
    >
      <div
        className="grid gap-3 px-4 py-3 text-xs font-semibold uppercase tracking-wide"
        style={{
          gridTemplateColumns: COLUMNS,
          minWidth: ROW_MIN,
          color: 'var(--text-secondary)',
          background: 'var(--surface-sunken)',
          borderBottom: '1px solid var(--border-default)'
        }}
      >
        <div>Date</div>
        <div>Time (IST)</div>
        <div>{zone_label(zone)}</div>
        <div>Meeting link</div>
        <div>Status</div>
        <div>Actions</div>
      </div>

      {runs.map((item) => {
        const badge = BADGES[item.status] || BADGES.draft;
        // Active slots only. A removed slot still exists in the database, but showing
        // its time and link would read as though it were live.
        const all_slots = item.slots || [];
        const slots = all_slots.filter((slot) => slot.status !== 'cancelled');
        const cancelled = item.status === 'cancelled';
        // A slot is only ever active or cancelled, so scheduled versus completed is
        // simply whether its instant has passed.
        const upcoming = slots.filter((slot) => moment(slot.start_utc).isAfter(now)).length;
        const finished = slots.length - upcoming;

        return (
          <div
            key={item.run_uuid}
            className="grid gap-3 px-4 py-3 text-sm items-start"
            style={{
              gridTemplateColumns: COLUMNS,
              minWidth: ROW_MIN,
              borderTop: '1px solid var(--border-default)',
              color: 'var(--text-primary)',
              opacity: cancelled ? 0.6 : 1
            }}
          >
            <div>
              <div className="font-semibold">
                {moment(item.run_date, 'YYYY-MM-DD').format('ddd D MMM YYYY')}
              </div>
              <div className="text-xs" style={{ color: 'var(--text-muted)' }}>
                <div>{item.duration_mins} min</div>
                {item.status === 'draft' ? (
                  slots.length > 0 && (
                    <div>
                      {slots.length} {slots.length === 1 ? 'slot' : 'slots'}
                    </div>
                  )
                ) : (
                  <>
                    {upcoming > 0 && <div>{upcoming} scheduled</div>}
                    {finished > 0 && <div>{finished} completed</div>}
                  </>
                )}
              </div>
            </div>

            <SlotLines slots={slots}>
              {(slot) => (
                <span key={slot.slot_uuid} style={{ color: 'var(--text-primary)' }}>
                  {ist_time(slot.start_utc)}
                </span>
              )}
            </SlotLines>

            <SlotLines slots={slots}>
              {(slot) => (
                <span
                  key={slot.slot_uuid}
                  className="whitespace-nowrap"
                  style={{ color: 'var(--text-secondary)' }}
                >
                  {in_zone(slot.start_utc, zone, item.run_date)}
                </span>
              )}
            </SlotLines>

            <SlotLines slots={slots}>
              {(slot) =>
                slot.meeting_link ? (
                  <a
                    key={slot.slot_uuid}
                    href={slot.meeting_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: 'var(--state-info)',
                      display: 'block',
                      minWidth: 0,
                      maxWidth: '100%',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}
                    title={slot.meeting_link}
                  >
                    {slot.meeting_link}
                  </a>
                ) : (
                  <span
                    key={slot.slot_uuid}
                    className="text-xs"
                    style={{ color: 'var(--state-warning-text)' }}
                  >
                    ⚠ No meeting link
                  </span>
                )
              }
            </SlotLines>

            <div>
              <span
                className="px-2 py-1 rounded text-xs font-semibold whitespace-nowrap"
                style={{ background: badge.bg, color: badge.fg }}
              >
                {badge.label}
              </span>
              {slots.some((slot) => slot.registration_count > 0) && (
                <div className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                  👤 {slots.reduce((sum, slot) => sum + (slot.registration_count || 0), 0)}
                </div>
              )}
            </div>

            <div className="flex gap-1 flex-nowrap" style={{ minWidth: 0 }}>
              <button
                type="button"
                className="ct-small-btn"
                style={COMPACT_BTN}
                onClick={() => onManage(item)}
              >
                {cancelled || item.status === 'completed' ? 'View' : 'Manage'}
              </button>
              {!cancelled && (
                <>
                  <button
                    type="button"
                    className="ct-small-btn"
                    style={COMPACT_BTN}
                    onClick={() => onDuplicate(item)}
                  >
                    Duplicate
                  </button>
                  <button
                    type="button"
                    className="ct-small-btn"
                    style={COMPACT_BTN}
                    onClick={() => onCancelRun(item)}
                  >
                    Cancel
                  </button>
                </>
              )}
              {/* Deletable once it can no longer be served. The server refuses either
                  one if it still holds registrations. */}
              {(item.status === 'draft' || cancelled) && (
                <button
                  type="button"
                  className="ct-small-btn"
                  style={COMPACT_BTN}
                  onClick={() => onDeleteRun(item)}
                >
                  Delete
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

RunsTable.propTypes = {
  runs: PropTypes.arrayOf(
    PropTypes.shape({
      run_uuid: PropTypes.string,
      run_date: PropTypes.string,
      duration_mins: PropTypes.number,
      status: PropTypes.string,
      slots: PropTypes.array
    })
  ).isRequired,
  onManage: PropTypes.func.isRequired,
  onDuplicate: PropTypes.func.isRequired,
  onCancelRun: PropTypes.func.isRequired,
  onDeleteRun: PropTypes.func.isRequired
};
